import { useState, useEffect, useCallback } from 'react';
import { quotesService } from '../services/api';
import { QuoteModal } from '../components/QuoteModal';
import { AppointmentModal } from '../components/AppointmentModal';
import { formatMoney } from '../utils/pricing';
import { formatApptDateLong } from '../utils/dateFormatters';
import './Quotes.css';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' }
];
const STATUS_LABEL = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' };

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null);      // { quote } | { create: true }
  const [bookModal, setBookModal] = useState(null); // { prefill }

  const load = useCallback(async () => {
    setLoading(true);
    const r = await quotesService.getAll(filter === 'all' ? {} : { status: filter });
    if (r.success) setQuotes(r.quotes || []);
    setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  // "Book job" from a quote: open the appointment form with the client, the
  // first job type on the quote and the quote itself pre-filled.
  function bookFrom(q) {
    setModal(null);
    const firstService = (q.items || []).find((it) => it.service_id);
    setBookModal({
      prefill: {
        customer_id: q.customer_id,
        service_id: firstService ? firstService.service_id : '',
        quote_id: q.id,
        admin_notes: `From quote ${q.quote_number}${q.title ? ` — ${q.title}` : ''}`
      }
    });
  }

  return (
    <div className="quotes-page">
      <div className="page-header">
        <h1 className="page-title">Quotes</h1>
        <button className="btn btn-primary" onClick={() => setModal({ create: true })}>+ New Quote</button>
      </div>

      <div className="quote-filters">
        {FILTERS.map((f) => (
          <button key={f.value} className={`quote-filter ${filter === f.value ? 'active' : ''}`} onClick={() => setFilter(f.value)}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="empty-state"><div className="loading-spinner"></div></div>
      ) : quotes.length === 0 ? (
        <div className="empty-state"><p>{filter === 'all' ? 'No quotes yet. Create one from a client, an appointment, or right here.' : 'Nothing here.'}</p></div>
      ) : (
        <div className="quote-list">
          {quotes.map((q) => (
            <button key={q.id} className="quote-row" onClick={() => setModal({ quote: q })}>
              <div className="quote-row-main">
                <h3>{q.quote_number} · {q.customer_name || 'No client'}</h3>
                <p className="text-muted">
                  {q.title || `${q.item_count} line item${Number(q.item_count) === 1 ? '' : 's'}`}
                  {Number(q.measurement_count) > 0 ? ` · ${q.measurement_count} area${Number(q.measurement_count) === 1 ? '' : 's'} on the map` : ''}
                  {q.job_number ? ` · ${q.job_number}` : ''}{q.booked_appointment_id ? ' · booked' : ''}
                </p>
              </div>
              <div className="quote-row-side">
                <strong>{formatMoney(q.total)}</strong>
                <span className="text-muted">{q.sent_at ? `Sent ${formatApptDateLong(q.sent_at)}` : formatApptDateLong(q.created_at)}</span>
              </div>
              <span className={`status-badge status-${q.effective_status}`}>{STATUS_LABEL[q.effective_status] || q.effective_status}</span>
            </button>
          ))}
        </div>
      )}

      <QuoteModal
        isOpen={!!modal}
        onClose={() => { setModal(null); load(); }}
        onSave={() => load()}
        onBook={bookFrom}
        quote={modal?.quote || null}
      />
      <AppointmentModal
        isOpen={!!bookModal}
        onClose={() => setBookModal(null)}
        onSave={() => { setBookModal(null); load(); }}
        prefill={bookModal?.prefill || null}
      />
    </div>
  );
}
